import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Input, Select, Button } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { T, always, and, assoc, compose, cond, defaultTo, equals, isNil, not, or, path, pick, prop, propOr, replace, trim, isNotNil } from 'ramda';
import { isNotNilOrEmpty, isTrue } from 'ramda-adjunct';
import { Components } from '@fasstech/flexiget';
import classNames from 'classnames';
import { Section, Title, ChevronBottom } from '../../../../components';
import UpdateGlobalVariableMutation from '../../../../_graphql/mutations/documents/UpdateGlobalVariableMutation';
import CreateGlobalVariableMutation from '../../../../_graphql/mutations/documents/CreateGlobalVariableMutation';
import { callbackSave, errorMessage, successMessage } from '../../../../utils/messageMutation';
import BaseModal from '../../../../components/BaseModal';
import useActionGuard from '../../../../hooks/useActionGuard';

const useGlobalVariable = ({ globalVariableId, globalVariable, scope, offerId, modalMode, setGlobalVariableCreated }) => {
  const globalVariableExist = isNotNil(globalVariableId);
  const [state, setState] = useState(defaultTo({}, globalVariable));

  const history = useHistory();
  const { loading, run } = useActionGuard();

  const isEdit = !isNil(globalVariableId);

  const isValid = isNotNilOrEmpty(prop('name', state)) && isNotNilOrEmpty(prop('type', state));

  const onSave = () => {
    if (isEdit) {
      const globalVariable = compose(
        (gv) => ({ ...gv, name: trim(prop('name', gv)) }),
        pick(['name', 'type'])
      )(state);
      run(`save-global-variable-${globalVariableId}`, (done) => UpdateGlobalVariableMutation({ globalVariableId, globalVariable }, done), callbackSave('variables-globales', 'La variable globale a bien été mise à jour.', history, scope, state, offerId));
    } else {
      const globalVariable = compose(
        assoc('offerId', offerId),
        assoc('scope', scope),
        assoc('name', trim(prop('name', state)))
      )(state);
      run(`save-global-variable-${offerId}`, (done) => CreateGlobalVariableMutation({ globalVariable }, done), (ok, error, globalVariable) => {
        if (ok && !error) {
          successMessage('variable globale', 'créée', false, true);
          if (!modalMode) {
            const destination = `/offre/${offerId || prop('offerId', state)}/edition`;
            history.push(destination);
          } else {
            setGlobalVariableCreated({
              ...state, id: prop('globalVariableId', globalVariable), name: prop('name', globalVariable), dates: { creation: new Date() }
            });
            setState({});
          }
        } else {
          if (equals('name_already_exist', error)) {
            errorMessage('Le nom indiqué existe déjà.');
          } else {
            errorMessage();
          }
        }
      });
    }
  };

  return {
    ...state,
    setState,
    isEdit,
    onSave,
    isValid,
    globalVariableExist,
    loading: propOr(false, `save-global-variable-${isEdit ? globalVariableId : offerId}`, loading)
  };
};

const GlobalVariableForm = ({
  globalVariableId,
  globalVariable,
  scope,
  offerId,
  preview,
  modalMode = false,
  setGlobalVariableCreated,
  isOpenCreateGlobalVariableModal,
  setIsOpenCreateGlobalVariableModal
}) => {
  const {
    isEdit,
    name,
    type,
    setState,
    onSave,
    isValid,
    globalVariableExist,
    loading
  } = useGlobalVariable({
    globalVariableId, globalVariable, scope, offerId, modalMode, setGlobalVariableCreated
  });
  const globalVariableEditable = and(or(isEdit, globalVariableExist), not(preview));
  const title = cond([
    [() => globalVariableEditable, always('Modifier la variable globale')],
    [() => isTrue(preview), always('Prévisualiser la variable globale')],
    [T, always('Créer une variable globale')]
  ])(globalVariable);

  const backToTitle = cond([
    [() => modalMode, always(null)],
    [isNotNil, always(`/offre/${offerId}/edition`)]
  ])(offerId);
  const onHide = () => setIsOpenCreateGlobalVariableModal(null);

  const form = (
    <div className={classNames('flex gap-4', { 'flex-col': modalMode })}>
      <Input
        value={name}
        placeholder="Nom de la variable globale"
        onChange={(e) => setState(assoc('name', replace(/\s/g, '', path(['target', 'value'], e))))}
        size="middle"
        disabled={preview}
      />
      <Components.Tooltip
        title={prop('isUsed', globalVariable) && 'Cette variable est actuellement utilisée, vous ne pouvez pas modifier son type.'}
      >
        <Select
          value={type}
          onChange={(v) => setState(assoc('type', v))}
          placeholder="Type de la variable globale"
          className={classNames({ 'w-300px': !modalMode, 'w-full': modalMode })}
          size="middle"
          suffixIcon={<ChevronBottom />}
          disabled={preview || prop('isUsed', globalVariable)}
        >
          <Select.Option value="text">Chaîne de caractère</Select.Option>
          <Select.Option value="number">Nombre</Select.Option>
          <Select.Option value="boolean">Booléen</Select.Option>
        </Select>
      </Components.Tooltip>
    </div>
  );

  return (
    <>
      {modalMode ? (
        <BaseModal
          title={title}
          onHide={onHide}
          visible={isOpenCreateGlobalVariableModal}
          onClick={onSave}
          disabled={!isValid}
          loading={loading}
        >
          {form}
        </BaseModal>
      ) : (
        <>
          <Title
            title={title}
            backTo={backToTitle}
          />
          <Section title="nom de la variable globale">
            {form}
          </Section>
          {!preview && (
            <Button
              onClick={onSave}
              disabled={!isValid}
              className="block mx-auto mt-4"
              size="large"
              type="primary"
              icon={<ThunderboltOutlined />}
              loading={loading}
            >
              {isEdit ? 'Mettre à jour la variable globale' : 'Créer la variable globale'}
            </Button>
          )}
        </>
      )}
    </>
  );
};

export default GlobalVariableForm;
